import { API_BASE } from "./apiConfig.js";

/**
 * Client HTTP de l'API de comptes. Toutes les requêtes incluent les cookies
 * (`credentials: 'include'`) pour transporter la session. Renvoie des objets
 * `{ ok, status, data }` plutôt que de lever, pour simplifier l'UI.
 */
// app.js attend `AccountSync.init()` (→ AccountService.me()) avant d'instancier le
// moindre jeu : un serveur qui accepte la connexion sans jamais répondre gèlerait
// tout le site sans ce plafond.
const TIMEOUT_MS = 8000;

export default class AccountService {
    static async request(method, path, body) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
        let res;
        try {
            res = await fetch(`${API_BASE}${path}`, {
                method,
                credentials: "include",
                headers: body ? { "Content-Type": "application/json" } : undefined,
                body: body ? JSON.stringify(body) : undefined,
                keepalive: true, // survit à la navigation immédiate (ex: clic sur "classement" en fin de partie)
                signal: controller.signal,
            });
        } catch {
            return { ok: false, status: 0, data: null }; // réseau indisponible ou timeout dépassé
        } finally {
            clearTimeout(timer);
        }
        let data = null;
        try {
            data = await res.json();
        } catch {
            /* corps vide / non-JSON */
        }
        return { ok: res.ok, status: res.status, data };
    }

    static me() {
        return this.request("GET", "/auth/me");
    }
    /** `extra` : `{ reminderEmail, lang, timezone }` (rappels du Défi du jour, case décochée par défaut). */
    static register(email, password, username, extra = {}) {
        return this.request("POST", "/auth/register", { email, password, ...(username ? { username } : {}), ...extra });
    }
    /** `lang` complète la langue d'un compte qui n'en a pas encore (e-mails dans sa langue). */
    static login(email, password, lang) {
        return this.request("POST", "/auth/login", { email, password, ...(lang ? { lang } : {}) });
    }
    static logout() {
        return this.request("POST", "/auth/logout");
    }
    static forgotPassword(email, lang) {
        return this.request("POST", "/auth/forgot-password", { email, lang });
    }
    static resetPassword(token, password) {
        return this.request("POST", "/auth/reset-password", { token, password });
    }
    static getSync() {
        return this.request("GET", "/sync");
    }
    static putSync(data) {
        return this.request("PUT", "/sync", { data });
    }
    static deleteSync(keys) {
        return this.request("DELETE", "/sync", keys ? { keys } : {});
    }
    static getPreferences() {
        return this.request("GET", "/account/preferences");
    }
    static patchPreferences(payload) {
        return this.request("PATCH", "/account/preferences", payload);
    }
    static deleteAccount(password) {
        return this.request("DELETE", "/account", { password });
    }
    /**
     * Désinscription par e-mail, sans connexion (lien du mail). `list` : "reminders"
     * (rappels du Défi du jour, par défaut) ou "news" (nouveautés).
     */
    static unsubscribeEmail(token, list = "reminders") {
        return this.request("POST", "/email/unsubscribe", { token, list });
    }
    static resubscribeEmail(token, list = "reminders") {
        return this.request("POST", "/email/resubscribe", { token, list });
    }
    static pushKey() {
        return this.request("GET", "/push/key");
    }
    static pushSubscribe(payload) {
        return this.request("POST", "/push/subscribe", payload);
    }
    static pushUnsubscribe(endpoint) {
        return this.request("POST", "/push/unsubscribe", { endpoint });
    }
    /** Score anonyme du Défi du jour (public), pour le « mieux que X % ». */
    static postDailyScore(device, day, mode, score) {
        return this.request("POST", "/daily/score", { device, day, mode, score });
    }
    static getDailyStats(day, mode) {
        return this.request("GET", `/daily/stats?day=${encodeURIComponent(day)}&mode=${encodeURIComponent(mode)}`);
    }
    /** Endpoint public (aucune session requise) — voir PlayerIdentity.js. */
    static registerPlayer(id) {
        return this.request("POST", "/players", { id });
    }
    static patchAvatar(avatar) {
        return this.request("PATCH", "/account/avatar", avatar);
    }
    static patchProfile(payload) {
        return this.request("PATCH", "/account/profile", payload);
    }
}
